import { c as _c } from "react/compiler-runtime";
import { type RefObject, useEffect, useState } from 'react';

/**
 * useScrollFadeOut
 * Returns an opacity value [0..1] that linearly decreases as the element's bottom
 * approaches a viewport-aligned threshold, over a given fade distance.
 *
 * Example: opacity = 1 when bottom is >= threshold + fadeDistance; opacity = 0 at threshold.
 *
 * @param ref - element ref to observe
 * @param thresholdPx - viewport px (from top) where fading starts/ends
 * @param fadeDistancePx - px distance over which to interpolate opacity
 *
 * @returns number opacity in [0,1]
 */
export const useScrollFadeOut = (ref, thresholdPx, fadeDistancePx) => {
  const $ = _c(5);
  const [opacity, setOpacity] = useState(1);
  let t0;
  let t1;
  if ($[0] !== fadeDistancePx || $[1] !== ref || $[2] !== thresholdPx) {
    t0 = () => {
      const handleScrollOrResize = () => {
        if (!ref.current) {
          return;
        }
        const rect = ref.current.getBoundingClientRect();
        const distanceToThresholdPx = rect.bottom - thresholdPx;
        if (distanceToThresholdPx <= 0) {
          setOpacity(0);
          return;
        }
        if (distanceToThresholdPx >= fadeDistancePx) {
          setOpacity(1);
          return;
        }
        setOpacity(distanceToThresholdPx / fadeDistancePx);
      };
      handleScrollOrResize();
      window.addEventListener("scroll", handleScrollOrResize, {
        passive: true
      });
      window.addEventListener("resize", handleScrollOrResize);
      return () => {
        window.removeEventListener("scroll", handleScrollOrResize);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    };
    t1 = [ref, thresholdPx, fadeDistancePx];
    $[0] = fadeDistancePx;
    $[1] = ref;
    $[2] = thresholdPx;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return opacity;
};